<template>
	<div class="p-component p-fluid p-formgrid p-grid">
		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ trainingKey.description }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<InputText type="text" v-model="trainingKey.value.value" disabled />
			<small v-if="!(trainingKey.value.value !== '' && trainingKey.value.value !== null)" class="p-error">{{$t("Squeeze.Training.TrainingKey")}}</small>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.DocumentClasses.Table") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="indicator.tableId"
				:options="tables"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				@change="getIndicators"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Description") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText type="text" v-model="indicator.description" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="indicator.locatorId ? $t('Squeeze.Training.OptionalFallback') : $t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Validation.General.Position") }} {{ $t("Squeeze.Training.KeyWord") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText type="text" v-model="indicator.keyWordPattern" :placeholder="$t('Squeeze.Training.KeyWord')" @focus="onFocusField('indicatorKeyWordPattern')" />
				<Button class="active" icon="mdi mdi-auto-fix" @click="onFocusField('indicatorKeyWordPattern')" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="$t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Training.Locator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="indicator.locatorId"
				:options="allLocators"
				optionLabel="description"
				optionValue="id"
				:showClear="true"
				:placeholder="$t('Squeeze.Training.SelectLocator')"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Region") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<div class="p-inputgroup">
				<span
					v-if="regionBadge"
					class="p-inputtext p-component region-display region-display--set region-display--clickable"
					@click="markCurrentRegion"
				>✓ {{ regionBadge }}</span>
				<span
					v-else
					class="p-inputtext p-component region-display region-display--unset"
				>{{ $t('Squeeze.Training.RegionNotSet') }}</span>
				<Button icon="mdi mdi-vector-rectangle" @click="onFocusField('indicatorRegion')" />
			</div>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.IgnoreSpaces") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="indicator.ignoreWhiteSpaces" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<span v-tooltip.top="!canTrain ? $t('Squeeze.Training.PatternOrLocatorRequired') : null">
				<Button
					:label="indicator.id ? $t('Squeeze.Training.Retrain') : $t('Squeeze.Training.Train')"
					:disabled="!canTrain"
					@click="trainIndicator"
				/>
			</span>
		</div>
	</div><br/>

	<div class="inner-content">
		<FormPositionTrainingTable
			tableOrigin="indicators"
			:tableData="allIndicators"
			:loading="loading"
			@testData="testIndicator"
			@changeData="changeIndicator"
			@deleteData="deleteIndicator"
			@onMarkRegion="onMarkRegion"
		/>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, onBeforeMount, onMounted, PropType, reactive, ref, watch, computed} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {DocumentField, DocumentTable, FormTrainingItemRegionAnchor, DocumentLocator} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import FormPositionTrainingTable from "@/apps/squeeze/components/FormPositionTrainingTable.vue";

export default defineComponent({
	name: "FormTrainingItemIndicator",
	components: {
		InputText,
		Dropdown,
		Button,
		FormPositionTrainingTable,
	},
	props: {
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		tables: {
			type: Array as PropType<DocumentTable[]>,
			default: () => [],
		},
		indicatorOfFormTraining: {
			type: Object as PropType<FormTrainingItemRegionAnchor>,
			default: () => ({}),
		},
		booleanOptions: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
	},
	emits: [
		'testIndicator',
		'onFocusField',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current training key field */
		const trainingKey = reactive<DocumentField>({});

		/** Current indicator */
		const indicator = reactive<FormTrainingItemRegionAnchor>({
			id: undefined,
			tableId: undefined,
			description: '',
			keyWordPattern: '',
			keyWordRegion: {
				page: 0,
				x0: 0,
				y0: 0,
				x1: 0,
				y1: 0,
			},
			ignoreWhiteSpaces: false,
			locatorId: undefined,
		});

		/** Show Loading on load data */
		const loading = ref(false);

		/** All indicators */
		const allIndicators = ref<FormTrainingItemRegionAnchor[]>([]);

		/** All locators */
		const allLocators = ref<DocumentLocator[]>([]);

		/** Form Training API endpoint */
		const formTrainingApi = ClientManager.getInstance().squeeze.formTraining;

		/** Locator API endpoint */
		const locatorApi = ClientManager.getInstance().squeeze.locator;

		const getAllLocators = () => {
			locatorApi.getAllLocators()
				.then(data => { allLocators.value = data; })
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ': ' + err.message);
				}));
		}

		const regionBadge = computed(() => {
			const r = indicator.keyWordRegion;
			if (!r || (r.x0 === 0 && r.y0 === 0 && r.x1 === 0 && r.y1 === 0)) return null;
			const fmt = (n: number | undefined) => Number((n ?? 0).toFixed(1));
			return `p.${r.page ?? 0} · (x0:${fmt(r.x0)}, y0:${fmt(r.y0)}) → (x1:${fmt(r.x1)}, y1:${fmt(r.y1)})`;
		});

		const canTrain = computed(() =>
			!!indicator.tableId
			&& (indicator.keyWordPattern !== '' || !!indicator.locatorId)
			&& !!regionBadge.value
			&& trainingKey.value?.value !== ''
			&& trainingKey.value?.value != null
		);

		/**
		 * Triggered when a field is focused
		 * @param {string} fieldName
		 */
		const onFocusField = (fieldName: string) => {
			emit("onFocusField", fieldName);
		}

		/** Reset indicator */
		const resetIndicator = () => {
			Object.assign(indicator, {
				id: undefined,
				tableId: undefined,
				description: '',
				keyWordPattern: '',
				keyWordRegion: { page: 0, x0: 0, y0: 0, x1: 0, y1: 0 },
				ignoreWhiteSpaces: false,
				locatorId: undefined,
			});
		}

		/** Get all indicators */
		const getIndicators = () => {
			if (!trainingKey.value!.value) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Viewer.Error.NoCreditorField'));
				return;
			}

			loading.value = true;

			let promise;
			if (indicator.tableId) {
				promise = formTrainingApi.getFormTableItemIndicator(props.documentClassId, trainingKey.value!.value!, indicator.tableId);
			} else {
				promise = formTrainingApi.getFormTableItemIndicator(props.documentClassId, trainingKey.value!.value!);
			}
			promise
				.then(data => {
					allIndicators.value = data;
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Train or retrain an indicator */
		const trainIndicator = () => {
			loading.value = true;

			let promise;
			if (indicator.id) {
				promise = formTrainingApi.retrainFormTableItemIndicator(props.documentClassId, indicator.id, indicator, true);
			} else {
				promise = formTrainingApi.trainFormTableItemIndicator(props.documentClassId, indicator.tableId!, trainingKey.value!.value!, indicator, false);
			}

			promise
				.then(() => {
					getIndicators();
					resetIndicator();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Test an indicator
		 * @param rowData
		 */
		const testIndicator = (rowData: any) => {
			emit("testIndicator", rowData);
		}

		/**
		 * Change an Indicator
		 * @param data
		 */
		const changeIndicator = (data: any) => {
			Object.assign(indicator, { locatorId: undefined, ...data });
		}

		/**
		 * Deletes an Indicator
		 * @param data
		 */
		const deleteIndicator = (data: any) => {
			loading.value = true;
			formTrainingApi.deleteFormTableItemIndicator(props.documentClassId, data.id)
				.then(() => {
					getIndicators();
					resetIndicator();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Mark the current form region in the viewer */
		const markCurrentRegion = () => {
			if (indicator.keyWordRegion) {
				emit("onMarkRegion", [indicator.keyWordRegion]);
			}
		}

		/**
		 * Set marker regions by clicking a row
		 * @param event
		 */
		const onMarkRegion = (event: {originalEvent: PointerEvent; data: any; index: number}) => {
			const row = event.data;
			emit("onMarkRegion", [row.keyWordRegion]);
		}

		onMounted(() => {
			Object.assign(indicator, props.indicatorOfFormTraining);
			getIndicators();
			resetIndicator();
			getAllLocators();
		})

		onBeforeMount(() => {
			Object.assign(trainingKey, props.trainingKeyField);
		})

		watch(
			() => [props.indicatorOfFormTraining.keyWordPattern, props.indicatorOfFormTraining.keyWordRegion],
			() => {
				indicator.keyWordPattern = props.indicatorOfFormTraining.keyWordPattern;
				indicator.keyWordRegion = props.indicatorOfFormTraining.keyWordRegion;
			},
			{ deep: true }
		)

		return {
			getTranslatedDescription,
			t,
			toast,
			trainingKey,
			indicator,
			loading,
			allIndicators,
			allLocators,
			regionBadge,
			canTrain,
			onFocusField,
			markCurrentRegion,
			getIndicators,
			getAllLocators,
			trainIndicator,
			testIndicator,
			changeIndicator,
			deleteIndicator,
			onMarkRegion,
		}
	},
});
</script>

<style scoped>

.inner-content {
	height: calc(100% - 4.5rem);
}

.region-display {
	background: var(--surface-ground);
	font-family: monospace;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
}

.region-display--set {
	color: var(--green-400);
}

.region-display--clickable {
	cursor: pointer;
}

.region-display--clickable:hover {
	background: var(--surface-hover);
}

.region-display--unset {
	color: var(--red-400);
	font-style: italic;
}

</style>
